import {PayloadAction, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {makeAlertQueryKey} from 'src/modules/profile/user/tabs/content/alerts/util';
import {
    getUserGeneralAlertFailure,
    getUserGeneralAlertRequest,
    getUserGeneralAlertSuccess,
} from 'src/openapi/admin/apis/UserGeneralAlertApi';
import {AlertSortField} from 'src/openapi/admin/models/AlertSortField';
import {GeneralAlert} from 'src/openapi/admin/models/GeneralAlert';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import type {AlertPageRequest} from 'src/openapi/admin/models/AlertPageRequest';


const alertsAdapter = createEntityAdapter<GeneralAlert, string>({
    selectId: alert => alert.contentId,
});

type QueryState = {
    ids: string[],
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
    requestId?: string,
};

export type UserPageState = {
    userPage: {
        tabs: {
            content: {
                alerts: AlertsSliceState,
            },
        },
    },
};


type AlertsSliceState = {
    queries: Record<string, QueryState>,
    criteriaByScope: Record<string, AlertPageRequest>,
} & ReturnType<typeof alertsAdapter.getInitialState>;

const initialState: AlertsSliceState = {
    ...alertsAdapter.getInitialState(),
    queries: {},
    criteriaByScope: {},
};

export const defaultAlertCriteria: AlertPageRequest = {
    pagination: {currentPage: 1, pageSize: 15},
    sortDirection: SortDirection.ASC,
    alertSortField: AlertSortField.ALERT_NAME,
};

export const alertsSlice = createSlice({
    name: 'alerts',
    initialState,
    reducers: {
        setScopeCriteria: (
            state,
            action: PayloadAction<{scopeKey: string, patch: Partial<AlertPageRequest>}>,
        ) => {
            const {scopeKey, patch} = action.payload;
            const prev = state.criteriaByScope[scopeKey] || defaultAlertCriteria;

            const resetPageNeeded =
                patch.alertName !== undefined ||
                patch.generalAlertTypes !== undefined ||
                patch.searchListType !== undefined ||
                patch.frequency !== undefined;

            state.criteriaByScope[scopeKey] = {
                ...prev,
                ...patch,
                pagination: {
                    ...prev.pagination,
                    ...(patch.pagination || {}),
                    currentPage: resetPageNeeded
                        ? 1
                        : (patch.pagination?.currentPage ?? prev.pagination?.currentPage ?? 1),
                },
            };
        },
        resetScopeCriteria: (state, action: PayloadAction<string>) => {
            state.criteriaByScope[action.payload] = defaultAlertCriteria;
        },
        invalidateQuery: (state, action: PayloadAction<string>) => {
            const query = state.queries[action.payload];
            if (query) {
                query.receivedAt = undefined;
            }
        },
        invalidateQueriesForUser: (state, action: PayloadAction<number>) => {
            const prefix = `${action.payload}::`;
            Object.keys(state.queries).forEach(key => {
                if (key.startsWith(prefix)) {
                    state.queries[key].status = 'idle';
                    state.queries[key].receivedAt = undefined;
                }
            });
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getUserGeneralAlertRequest, (state, action) => {
                const {userId, alertPageRequest} = action.payload.meta;
                const key = makeAlertQueryKey(userId, alertPageRequest);
                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'loading',
                };
            })
            .addCase(getUserGeneralAlertSuccess, (state, action) => {
                const {userId, alertPageRequest} = action.payload.meta;
                const {alerts, totalCount} = action.payload.data;
                const key = makeAlertQueryKey(userId, alertPageRequest);

                alertsAdapter.upsertMany(state, alerts);

                state.queries[key] = {
                    ids: alerts.map(a => a.contentId),
                    total: totalCount,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getUserGeneralAlertFailure, (state, action) => {
                const {userId, alertPageRequest} = action.payload.meta;
                const key = makeAlertQueryKey(userId, alertPageRequest);

                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {
    setScopeCriteria,
    resetScopeCriteria,
    invalidateQuery,
    invalidateQueriesForUser,
} = alertsSlice.actions;

export const alertsSelectors = alertsAdapter.getSelectors<{
    userPage: {tabs: {content: {alerts: AlertsSliceState}}},
}>(state => state.userPage.tabs.content.alerts);
